import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { GeoDetectionBanner } from './components/GeoDetectionBanner';

// Pages
import ExplorePage from './pages/ExplorePage';
import FeedPage from './pages/FeedPage';
import EventDetailPage from './pages/EventDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import TicketConfirmationPage from './pages/TicketConfirmationPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import MyTicketsPage from './pages/MyTicketsPage';
import OrganiserDashboard from './pages/OrganiserDashboard';
import AdminPanel from './pages/AdminPanel';
import RecurringSeriesPage from './pages/RecurringSeriesPage';
import MyDiscussionsPage from './pages/MyDiscussionsPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import LegalPage from './pages/LegalPage';
import OrganizerProfilePage from './pages/OrganizerProfilePage';
import './index.css';

function AppRouter() {
  const location = useLocation();

  // Detect session_id synchronously to prevent race conditions
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const isImmersive = location.pathname === '/feed';

  return (
    <div className="App min-h-screen bg-[#050505]">
      {!isImmersive && <Navbar />}
      {!isImmersive && <GeoDetectionBanner />}
      <main>
        <Routes>
          <Route path="/" element={<ExplorePage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/events/:id" element={<EventDetailPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<TicketConfirmationPage />} />
          <Route path="/checkout/cancel" element={<CheckoutCancelled />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/my-tickets" element={<MyTicketsPage />} />
          <Route path="/organiser" element={<OrganiserDashboard />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/recurring-series" element={<RecurringSeriesPage />} />
          <Route path="/my-discussions" element={<MyDiscussionsPage />} />
          <Route path="/account" element={<AccountSettingsPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/organizers/:id" element={<OrganizerProfilePage />} />
          <Route path="*" element={<ExplorePage />} />
        </Routes>
      </main>
      {!isImmersive && <Footer />}
    </div>
  );
}

function CheckoutCancelled() {
  return (
    <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center px-4">
      <div className="text-center">
        <p className="text-gray-400 text-lg mb-4">Payment was cancelled</p>
        <a href="/explore" className="btn-primary px-6 py-3 rounded text-sm font-bold inline-block">
          Back to Events
        </a>
      </div>
    </div>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
    <LanguageProvider>
      <CurrencyProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: '#0A0A0A',
                  border: '1px solid #262626',
                  color: '#fff',
                  fontFamily: 'Inter, sans-serif',
                },
              }}
            />
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </CurrencyProvider>
    </LanguageProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
